import { datadogRum } from '@datadog/browser-rum'
import { NfcAuth, NFC_AUTH_API_BASE_URL, VerifyTapResponse } from 'core/api/NfcAuth'
import { DatadogEncodingRun, EncodingValidationRequest, TmrTag } from 'core/api'
import { RfidReader } from 'core/shared'
import { CustomCommandTimeoutError, DEVICE_MANAGER_ANTENNA_TIMEOUT } from 'core/shared/RfidReader'
import { __, T } from 'core/shared/i18n'
import {
  CUSTOM_COMMANDS_OPERATION_TYPE,
  decToHex,
  putSpacesEveryByte,
  reverseString,
  sleep,
  urlParamsToObject
} from 'core/shared/utils'

export const EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE = {
  'mediol-40': { inventory: 10, step1: 8, step2: 9, getUid: 15 },
  'mediop-200u': { inventory: 3, step1: 12, step2: 14, getUid: 2 }
} as const

export const SHOULD_REVERSE_TAG_RESPONSES = new Set(['mediop-200u'])

export const NFC_AUTH_TIMEOUT_MS = 10000
export const NFC_AUTH_MAX_ATTEMPTS = 3
const NFC_AUTH_RETRY_DELAY_MS = 300
const NFC_TAG_COMMAND_TIMEOUT_MS = 2000

/**
 * The tag stopped answering mid-sequence (left the field), or answered 010F because it was power-cycled and
 * lost the auth session kept in its RAM. Not a verdict on authenticity: the whole sequence can be retried from scratch.
 */
export class NfcTagLostError extends Error {}

/**
 * A newer authentication started while this one was still running and aborted it: the read is stale,
 * callers must drop it without advancing their flow (the newer read owns it).
 */
export class NfcAuthSupersededError extends Error {}

/**
 * Per-page bookkeeping of the NFC authentications a page starts. A read releases the page's read state
 * (isDecodeNfc, overlay, validation flags) only if no newer read of the same page started an authentication
 * since: that one owns the state then. A read superseded by another page must release it, or the page stays locked.
 */
export class NfcReadTracker {
  private lastRun = 0

  /** Call when a read starts */
  begin() {
    let run = this.lastRun
    let superseded = false
    return {
      /** This read starts an NFC authentication (call it where the page sets isDecodeNfc) */
      authenticate: () => {
        run = ++this.lastRun
      },
      /** True when error means a newer authentication superseded this read: the caller drops the read */
      isSuperseded: (error: unknown) => {
        superseded = error instanceof NfcAuthSupersededError
        return superseded
      },
      /**
       * Runs release unless a newer read of the same page owns the read state. superseded: this read was
       * superseded by another page, so the overlay and validation flags it set must be released too.
       */
      release: (release: (superseded: boolean) => void) => {
        if (run === this.lastRun) release(superseded)
      }
    }
  }
}

/** ws: the socket opened by this authentication (undefined = the shared one). Only a timeout means the tag was lost */
const sendTagCommand = async (ws: WebSocket | undefined, command: object, waitForSuccess = true) => {
  try {
    return await RfidReader.sendWsCustomCommandForResponse(command, waitForSuccess, NFC_TAG_COMMAND_TIMEOUT_MS, ws)
  } catch (error) {
    if (error instanceof CustomCommandTimeoutError) throw new NfcTagLostError(error.message)
    throw error
  }
}

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new Error('NFC authentication aborted')
}

// The mutual-auth in progress: a new one aborts it, since both would send commands on RfidReader.customCommandsWs
let currentNfcAuthAbortController: AbortController | undefined

export type NfcAuthProtocol = 'mutualAuth2Step' | 'authNdefRollingCode' | 'no'

export interface NfcAuthOperationOptions {
  nfcAuthProtocol?: NfcAuthProtocol
  nfcAuthApiBaseUrl?: string
  nfcUserMemoryStartBlock?: number
  nfcUserMemoryOffsetBlock?: number
}

export interface ReadNfcTag {
  active: boolean
  block: number
  blockCount: number
}

export const isMutualAuth2StepEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  operation.options?.nfcAuthProtocol === 'mutualAuth2Step'

export const isAuthNdefRollingCodeEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  operation.options?.nfcAuthProtocol === 'authNdefRollingCode'

/** True when an NFC auth protocol other than `no` is active. */
export const isNfcAuthEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  isMutualAuth2StepEnabled(operation) || isAuthNdefRollingCodeEnabled(operation)

export const getNfcReadConfiguration = (operation: { options?: NfcAuthOperationOptions }): ReadNfcTag | undefined => {
  if (!isAuthNdefRollingCodeEnabled(operation)) return undefined
  return {
    active: true,
    block: operation.options?.nfcUserMemoryStartBlock ?? 0,
    blockCount: operation.options?.nfcUserMemoryOffsetBlock ?? 8
  }
}

export const getNfcTagReadErrors = (encodingValidation: EncodingValidationRequest) => ({
  nfcError: encodingValidation.identifiers.some((id) => id.identifierType === 'NFC_TAG' && id._status === 'ERROR'),
  nfcCollision: encodingValidation.identifiers.some(
    (id) => id.identifierType === 'NFC_TAG' && id._status === 'NFC_COLLISION'
  )
})

export const getNfcAuthApiBaseUrl = (operation?: { options?: NfcAuthOperationOptions }) =>
  operation?.options?.nfcAuthApiBaseUrl || NFC_AUTH_API_BASE_URL

export interface NfcMutualAuthSuccessResult {
  finalUid: string
  challengeString?: string
  challengeInHex?: string
}

export type NfcMutualAuthResult = NfcMutualAuthSuccessResult | false | undefined

export const prepareMutualAuthAntenna = async (antennaId: string) => {
  const antennas = RfidReader.getAntennas().filter((a) => a.reader?.settings?.supportedTagType === 'NFC')
  const currentAntenna = antennas.find((a) => a.id === antennaId)
  if (!currentAntenna) return

  const otherAntennasForSameReader = antennas.filter(
    (a) => a.reader.deviceType === currentAntenna.reader.deviceType && a.id !== antennaId
  )
  if (otherAntennasForSameReader.length === 0) return

  RfidReader.excludedAntennaIdsFromStopRequest = otherAntennasForSameReader.map((a) => a.id)
  for (const antenna of [currentAntenna, ...otherAntennasForSameReader]) {
    RfidReader.isManualStop = true
    await RfidReader.getDeviceManagerApi().post(
      `/api/v1/readers/antenna/${antenna.id}/stop?sessionId=${RfidReader.sessionId}`,
      {
        ...currentAntenna.reader,
        id: currentAntenna.reader.code,
        antennas
      },
      { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT }
    )
  }
  await sleep(200)
  await RfidReader.getDeviceManagerApi().post(
    `/api/v1/readers/antenna/${currentAntenna.id}/start-custom-operation?operationType=${CUSTOM_COMMANDS_OPERATION_TYPE}&sessionId=${RfidReader.sessionId}`,
    {
      ...currentAntenna.reader,
      id: currentAntenna.reader.code,
      antennas: antennas.filter((a) => a.id === currentAntenna.id)
    },
    { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT }
  )
  RfidReader.resetManualStop(10000)
}

export const parseFinalUid = (readerType: string, finalUidResult: string) =>
  SHOULD_REVERSE_TAG_RESPONSES.has(readerType)
    ? finalUidResult.substring(10, 26)
    : reverseString(finalUidResult.substring(2, 18)).toUpperCase()

export const normalizeTagResponse = (readerType: string, result: string) =>
  SHOULD_REVERSE_TAG_RESPONSES.has(readerType) ? reverseString(result) : result

/**
 * Polls the antenna for a collision-free inventory read, up to 5 attempts. Throws if a collision persists.
 * ws: the socket to use, by default the shared one.
 */
export const checkNfcCollision = async (readerType: string, signal?: AbortSignal, ws?: WebSocket): Promise<void> => {
  let collisionCount = 0
  let collision = true

  while (collisionCount < 5 && collision) {
    throwIfAborted(signal)
    const collisionCheck = JSON.parse(
      await sendTagCommand(
        ws,
        {
          command: putSpacesEveryByte('260100'),
          parameters: {
            expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].inventory
          }
        },
        false
      )
    )
    if (
      collisionCheck.errorDescription?.toLowerCase().includes('collision') ||
      collisionCheck.result?.toLowerCase().includes('collision')
    ) {
      collisionCount++
      await sleep(500)
    } else {
      collision = false
    }
  }

  if (collision) {
    throw new Error('NFC Collision detected')
  }
}

/**
 * One full attempt (fresh websocket, fresh challenge). Throws NfcTagLostError when the tag stops answering or
 * answers 010F, so the caller can retry from scratch; returns false when the tag was rejected otherwise.
 */
export const nfcMutualAuthentication2Step = async (
  tag: TmrTag,
  antennaId: string,
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<NfcMutualAuthResult> => {
  const tagId = tag.uid
  if (!tagId) return false

  const dateInMilliseconds = Date.now()
  const dateInHex = decToHex(dateInMilliseconds)
  const challenge = (BigInt(`0x${dateInHex}`) << BigInt(4)).toString(16).toUpperCase()

  const antenna = RfidReader.getAntennas().find((a) => a.id === antennaId)
  if (!antenna) {
    throw new Error('Antenna not found')
  }
  const readerType = antenna.reader.deviceType

  // an attempt aborted meanwhile (e.g. while preparing the antenna) must not open a socket over the newer one's
  throwIfAborted(signal)
  const ws = await RfidReader.startWsCustomCommands(antennaId)
  try {
    await checkNfcCollision(readerType, signal, ws)
    throwIfAborted(signal)

    const step1Res = await sendTagCommand(ws, {
      command: putSpacesEveryByte('02D816038000' + challenge),
      parameters: {
        expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step1
      }
    })
    throwIfAborted(signal)

    const step1ServerRes = await NfcAuth.challenge(
      normalizeTagResponse(readerType, step1Res.result),
      challenge,
      apiBaseUrl
    )
    throwIfAborted(signal)

    if (!step1ServerRes) return false

    const connectionId = step1ServerRes?.data?.connectionId ?? ''
    const apdu = step1ServerRes?.data?.APDU ?? ''

    if (!connectionId || !apdu) {
      throw new Error('No connection id or apdu from the server')
    }

    const step2Res = await sendTagCommand(ws, {
      command: putSpacesEveryByte('02D816039000' + apdu),
      parameters: {
        expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step2
      }
    })
    throwIfAborted(signal)

    if ((step2Res.result as string)?.toLowerCase().includes('error')) {
      throw new Error('NFC authentication error')
    }

    // ISO15693 generic error: either a non-authentic tag or a tag that was power-cycled after step1
    // (session lost). The caller retries; a non-authentic tag simply fails every attempt.
    if (['010F', '0F01'].includes(step2Res.result)) {
      throw new NfcTagLostError(`NFC tag answered ${step2Res.result} to step2`)
    }

    await NfcAuth.validate(normalizeTagResponse(readerType, step2Res.result), connectionId, apiBaseUrl)
    throwIfAborted(signal)

    // const getCxidCommand = '2023' + tagId + '0404'
    // const getCxidCommand = putSpacesEveryByte('02230404')
    // const cxidRes = await RfidReader.sendWsCustomCommandForResponse({
    //   command: getCxidCommand,
    //   parameters: {
    //     expectedResponseLength: 21,
    //   },
    // })

    // if (!cxidRes.result || cxidRes.result === '000000000000000000000000000000000000000000') return false
    // const finalCxid = splitAndConvertASCII(cxidRes.result)

    const finalUidRes = await sendTagCommand(ws, {
      command: putSpacesEveryByte('022B'),
      parameters: {
        expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].getUid
      }
    })
    throwIfAborted(signal)

    if ((finalUidRes.result as string)?.toLowerCase().includes('collision')) {
      throw new Error('NFC collision detected')
    }

    const parsedFinalUid = parseFinalUid(readerType, finalUidRes.result)

    return {
      finalUid: parsedFinalUid,
      challengeString: dateInMilliseconds.toString(),
      challengeInHex: challenge
    }
  } finally {
    // An aborted (timed out) attempt can settle after a newer one opened its own websocket: only close ours.
    if (RfidReader.customCommandsWs === ws) {
      await RfidReader.stopWsCustomCommands(false, antennaId)
    } else {
      ws.close()
    }
  }
}

/**
 * Retries the whole mutual-auth sequence only while the tag keeps getting lost (or answers 010F) mid-way:
 * any other outcome (success, rejection, server or reader error) ends it at once.
 * Retrying never weakens the check: only a full, server-validated sequence returns a success.
 */
const nfcMutualAuthentication2StepWithRetry = async (
  tag: TmrTag,
  antennaId: string,
  apiBaseUrl: string,
  signal: AbortSignal
): Promise<NfcMutualAuthResult> => {
  await prepareMutualAuthAntenna(antennaId)
  for (let attempt = 1; ; attempt++) {
    try {
      return await nfcMutualAuthentication2Step(tag, antennaId, apiBaseUrl, signal)
    } catch (error) {
      if (!(error instanceof NfcTagLostError)) throw error
      // Out of attempts with the tag still unreachable: report it as not authenticated.
      if (attempt >= NFC_AUTH_MAX_ATTEMPTS) return false
      console.warn(`NFC mutual auth attempt ${attempt} lost the tag, retrying`, error)
    }
    await sleep(NFC_AUTH_RETRY_DELAY_MS)
    throwIfAborted(signal)
  }
}

export const nfcAuthNdefRollingCode = async (
  tag: TmrTag,
  apiBaseUrl: string
): Promise<NfcMutualAuthSuccessResult | false> => {
  if (!tag.userMemory) return false

  let body: Record<string, string | number>
  try {
    body = urlParamsToObject(tag.userMemory)
  } catch {
    return false
  }

  if (Object.keys(body).length === 0) return false

  const verifyTap: VerifyTapResponse = await NfcAuth.verifyTap(body, apiBaseUrl)
  if (verifyTap.verificationStatus === 'OK' && verifyTap.verificationData?.uid) {
    return { finalUid: verifyTap.verificationData.uid }
  }
  return false
}

const applyNfcAuthResult = (
  encodingValidation: EncodingValidationRequest,
  _tag: TmrTag,
  result: NfcMutualAuthResult,
  datadogEncodingSession?: DatadogEncodingRun
) => {
  if (result === false) {
    encodingValidation.identifiers.forEach((element) => {
      if (element.identifierType === 'NFC_TAG') {
        element._status = 'ERROR'
        element._error = __(T.error.tag_authentication_error)
      }
    })
    if (datadogEncodingSession) {
      datadogEncodingSession.errors?.push({
        error: 'nfcTagErrorOrNotAuthentic',
        details: { nfcAuthTimeoutOffsetMs: Date.now() - datadogEncodingSession.startTime.getTime() }
      })
      datadogRum.addAction('err_nfcTagErrorOrNotAuthentic', datadogEncodingSession)
    }
  } else if (result === undefined) {
    encodingValidation.identifiers = encodingValidation.identifiers.filter((element) => element.code !== _tag.uid)
  } else {
    _tag.uid = result.finalUid
  }
}

export const readNfcAuthenticatedTag = async (
  encodingValidation: EncodingValidationRequest,
  _tag: TmrTag,
  operationConfig: { options?: NfcAuthOperationOptions },
  antennaId: string,
  datadogEncodingSession?: DatadogEncodingRun
) => {
  const apiBaseUrl = getNfcAuthApiBaseUrl(operationConfig)
  // On timeout the sequence below keeps running: abort it so it can't apply a late result
  // or keep sending commands to the reader.
  const abortController = new AbortController()
  currentNfcAuthAbortController?.abort()
  currentNfcAuthAbortController = abortController
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    const res = await Promise.race([
      new Promise<boolean>((_, reject) => {
        timeoutId = setTimeout(() => {
          abortController.abort()
          reject(new Error(__(T.error.authentication_timeout)))
        }, NFC_AUTH_TIMEOUT_MS)
      }),
      (async () => {
        let result: NfcMutualAuthResult

        if (isAuthNdefRollingCodeEnabled(operationConfig)) {
          result = await nfcAuthNdefRollingCode(_tag, apiBaseUrl)
        } else {
          result = await nfcMutualAuthentication2StepWithRetry(_tag, antennaId, apiBaseUrl, abortController.signal)
        }
        throwIfAborted(abortController.signal)

        applyNfcAuthResult(encodingValidation, _tag, result, datadogEncodingSession)
        // false = not authenticated: callers must not go on and assign the tag, which would overwrite the
        // NFC identifier's ERROR status (EncodingExtensions.onTagRead -> assignTagIdentifier).
        return result !== false
      })()
    ])
    return res
  } catch (error: any) {
    console.warn('readNfcAuthenticatedTag error', error)
    // Superseded by a newer authentication: identifiers left alone, callers drop the read in their catch
    if (abortController.signal.aborted && currentNfcAuthAbortController !== abortController) {
      throw new NfcAuthSupersededError('NFC authentication superseded by a newer one')
    }
    if (error.message?.includes('Collision')) {
      if (!encodingValidation.identifiers.some((id) => id.identifierType === 'NFC_TAG')) {
        encodingValidation.identifiers.push({
          code: _tag.uid,
          identifierType: 'NFC_TAG'
        })
      }
      encodingValidation.identifiers.forEach((element) => {
        if (element.identifierType === 'NFC_TAG') {
          element._status = 'NFC_COLLISION'
          element._error = __(T.custom.tooManyTags)
        }
      })
      return false
    }

    encodingValidation.identifiers.forEach((element) => {
      if (element.identifierType === 'NFC_TAG') {
        element._status = 'ERROR'
        element._error = error?.message ? error.message : error
      }
    })
    return false
  } finally {
    clearTimeout(timeoutId)
    if (currentNfcAuthAbortController === abortController) currentNfcAuthAbortController = undefined
  }
}
